/* ==========================================================================
   Tire Rotation Pattern Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-trp]');
  if (!root) return;

  var INTERVAL_MILES = 6000;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    var staggered = !!input.staggered;
    var directional = input.tread === 'directional';
    var spare = !!input.spare;

    var pattern, detail, key = 'good';

    if (staggered && directional) {
      pattern = 'Cannot be rotated';
      detail = 'Directional tread means each tire must stay on its own side. Staggered sizing means it must stay on its own axle. Between them there is nowhere for any tire to go.';
      key = 'now';
    } else if (staggered) {
      pattern = 'Side to side only';
      detail = 'Swap left and right on each axle. Sizes differ front to rear, so nothing can cross axles. This only works if the tires are not directional.';
      key = 'monitor';
    } else if (directional) {
      pattern = 'Front to back, same side';
      detail = 'Each tire keeps its side of the vehicle and swaps front to rear. Left front goes to left rear, right front to right rear, and back again.';
    } else if (input.drive === 'fwd') {
      pattern = 'Forward cross';
      detail = 'Fronts go straight back. Rears cross to the opposite front. Left rear to right front, right rear to left front.';
    } else if (input.drive === 'rwd') {
      pattern = 'Rearward cross';
      detail = 'Rears go straight forward. Fronts cross to the opposite rear. Left front to right rear, right front to left rear.';
    } else {
      pattern = 'X pattern';
      detail = 'Every tire crosses to the opposite corner. Left front to right rear, right front to left rear, and the same for the rears.';
    }

    var warnings = [];
    if (spare && !staggered && !directional) {
      detail += ' With a full size matching spare, work it into the rotation as a fifth position so all five wear together.';
    } else if (spare && (staggered || directional)) {
      warnings.push('A full size spare cannot be worked into a directional or staggered rotation without breaking the rules that constrain it.');
    }

    if (input.drive === 'awd') {
      warnings.push('All wheel drive is the least forgiving of mismatched tire diameters. A worn tire on one corner makes the centre differential or transfer case work continuously, and that is an expensive failure. Rotate on schedule and replace all four together.');
    }

    var due = isFinite(input.last) ? input.last : 0;
    var overdue = due - INTERVAL_MILES;
    var status;
    if (key !== 'good') {
      status = { key: key, label: pattern, note: detail };
    } else if (overdue > 3000) {
      status = { key: 'now', label: pattern,
                 note: comma(Math.round(due)) + ' miles since the last rotation. Well overdue.' };
    } else if (overdue > 0) {
      status = { key: 'soon', label: pattern,
                 note: comma(Math.round(due)) + ' miles since the last rotation. Due now.' };
    } else {
      status = { key: 'good', label: pattern,
                 note: comma(Math.round(INTERVAL_MILES - due)) + ' miles until the next rotation.' };
    }

    var perYear = isFinite(input.miles) && input.miles > 0 ? input.miles / INTERVAL_MILES : 2;

    return {
      ok: true, errors: [], warnings: warnings, status: status,
      figures: [
        { label: 'Pattern', value: pattern, sub: detail },
        { label: 'Rotate every', value: comma(INTERVAL_MILES) + ' miles',
          sub: 'About ' + num(perYear, 1) + ' times a year at ' + comma(input.miles || 12000) + ' miles annually. Line it up with oil changes.' }
      ],
      rows: [
        { head: 'Your setup' },
        { label: 'Drivetrain', value: input.drive.toUpperCase() },
        { label: 'Tread', value: directional ? 'Directional' : 'Symmetric or asymmetric' },
        { label: 'Fitment', value: staggered ? 'Staggered' : 'Square, same all round' },
        { label: 'Spare', value: spare ? 'Full size, matching' : 'Not included' },
        { head: 'Schedule' },
        { label: 'Interval', value: comma(INTERVAL_MILES) + ' miles' },
        { label: 'Since last', value: comma(Math.round(due)) + ' miles' },
        { label: 'Status', value: overdue > 0 ? 'Overdue by ' + comma(Math.round(overdue)) : 'In date' },
        { head: 'The standard patterns' },
        { label: 'Front wheel drive', value: 'Forward cross' },
        { label: 'Rear wheel drive', value: 'Rearward cross' },
        { label: 'All wheel drive', value: 'X pattern' },
        { label: 'Directional', value: 'Front to back, same side' },
        { label: 'Staggered', value: 'Side to side only' }
      ]
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "drive",
          "key": "d",
          "type": "select"
      },
      {
          "name": "tread",
          "key": "t",
          "type": "select"
      },
      {
          "name": "staggered",
          "key": "s",
          "type": "checkbox"
      },
      {
          "name": "spare",
          "key": "sp",
          "type": "checkbox"
      },
      {
          "name": "miles",
          "key": "m",
          "type": "number"
      },
      {
          "name": "last",
          "key": "l",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-trp__figure">'
        + '<div class="pc-trp__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-trp__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-trp__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-trp__row pc-trp__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-trp__row">'
        + '<span class="pc-trp__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-trp__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_TRP = { compute: compute };
})();
